<template>
  <transition name="fading">
    <div v-if="show" class="overlay" @click.self="closeModal">
      <ImageViewer
        v-model:show="showImageViewer"
        :image-url="mediaDetail.SignedUrl || ''"
        :width="1000"
        :height="720"
      />

      <div class="detail-card">
        <div class="detail-header">
          <div class="header-copy">
            <p class="detail-kicker">Feedback Detail</p>
            <div class="header-title-row">
              <h2>#{{ feedback.FeedbackID }}</h2>
              <span class="outcome-pill" :class="outcomePillClass">{{ outcomeLabel }}</span>
              <span class="grade-pill">{{ feedback.FeedbackGrade || 'Unspecified' }}</span>
            </div>
            <p class="detail-subtitle">
              {{ feedback.UserName || feedback.Username || feedback.UserUuid || 'Unknown user' }}
              <span v-if="feedback.Username" class="subtitle-meta">@{{ feedback.Username }}</span>
              <span v-if="feedback.created_at" class="subtitle-meta">• {{ formatDateTime(feedback.created_at) }}</span>
            </p>
          </div>
          <button class="close-button" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="hero-media">
          <div class="media-shell">
            <button
              v-if="mediaDetail.SignedUrl"
              type="button"
              class="media-button"
              @click="showImageViewer = true"
            >
              <img :src="mediaDetail.SignedUrl" :alt="mediaDetail.FileName || feedback.MediaFileName || 'Feedback media'">
              <span class="media-overlay">
                <span class="media-overlay-label">
                  <i class="fas fa-search-plus"></i> Zoom
                </span>
              </span>
            </button>
            <div v-else-if="isMediaLoading" class="media-placeholder">
              <i class="fas fa-spinner fa-spin"></i>
              <span>Loading image…</span>
            </div>
            <div v-else class="media-placeholder">
              <i class="fas fa-image"></i>
              <span>No renderable image</span>
            </div>
          </div>
        </div>

        <div class="detail-sections">
          <div class="section-card">
            <div class="section-header">
              <span class="section-title">Feedback</span>
            </div>
            <div class="kv-grid">
              <div class="kv">
                <span class="kv-label">Outcome</span>
                <span class="kv-value">
                  <span class="outcome-pill" :class="outcomePillClass">{{ outcomeLabel }}</span>
                </span>
              </div>
              <div class="kv">
                <span class="kv-label">Abnormality</span>
                <span class="kv-value">{{ feedback.FeedbackAbnormality || '-' }}</span>
              </div>
              <div class="kv">
                <span class="kv-label">Created by</span>
                <span class="kv-value mono">{{ feedback.created_by || '-' }}</span>
              </div>
              <div class="kv">
                <span class="kv-label">Image size</span>
                <span class="kv-value">{{ imageSizeLabel }}</span>
              </div>
              <div class="kv kv-span">
                <span class="kv-label">Comments</span>
                <span class="kv-value notes">{{ feedback.FeedbackComments || '-' }}</span>
              </div>
            </div>
          </div>

          <div class="section-card">
            <div class="section-header">
              <span class="section-title">Context</span>
            </div>
            <div class="kv-grid">
              <div class="kv kv-span">
                <span class="kv-label">User</span>
                <span class="kv-value">
                  {{ feedback.UserName || feedback.Username || '-' }}
                  <span v-if="feedback.Username" class="inline-meta">@{{ feedback.Username }}</span>
                  <span v-if="feedback.UserUuid" class="sub-meta mono">{{ feedback.UserUuid }}</span>
                </span>
              </div>
              <div class="kv kv-span">
                <span class="kv-label">Organization</span>
                <span class="kv-value">
                  {{ feedback.OrganizationName || '-' }}
                  <span v-if="feedback.OrganizationUuid" class="sub-meta mono">{{ feedback.OrganizationUuid }}</span>
                </span>
              </div>
              <div class="kv kv-span">
                <span class="kv-label">Location</span>
                <span class="kv-value">
                  {{ feedback.LocationName || '-' }}
                  <span v-if="feedback.LocationUuid" class="sub-meta mono">{{ feedback.LocationUuid }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import ImageViewer from '@/components/ImageViewer.vue'
import { getFeedbackMedia } from '@/services/feedbackServices'

export default {
  name: 'FeedbackDetailModal',
  components: {
    ImageViewer,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    feedback: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  data() {
    return {
      showImageViewer: false,
      isMediaLoading: false,
      mediaDetail: {},
      lastFetchedMediaUuid: '',
      mediaFetchInFlight: null,
    }
  },
  computed: {
    outcomeLabel() {
      const label = this.feedback?.OutcomeLabel
      if (label) return label
      const raw = (this.feedback?.FeedbackOutcome || '').toString().trim().toLowerCase()
      if (raw === 'good' || raw === 'positive') return 'Positive'
      if (raw === 'bad' || raw === 'negative') return 'Negative'
      return this.feedback?.FeedbackOutcome || 'Unknown'
    },
    outcomePillClass() {
      return this.outcomeLabel === 'Positive' ? 'positive-pill' : 'negative-pill'
    },
    imageSizeLabel() {
      const width = this.mediaDetail.Width || this.feedback.ImageWidth
      const height = this.mediaDetail.Height || this.feedback.ImageHeight
      if (!width || !height) {
        return '-'
      }
      return `${width} x ${height}`
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value && this.feedback?.MediaUuid) {
          this.maybeFetchMedia()
        }

        if (!value) {
          this.showImageViewer = false
          this.mediaDetail = {}
          this.isMediaLoading = false
          this.lastFetchedMediaUuid = ''
          this.mediaFetchInFlight = null
        }
      },
    },
    'feedback.MediaUuid'(value) {
      if (this.show && value) {
        this.maybeFetchMedia()
      }
    },
  },
  methods: {
    maybeFetchMedia() {
      const mediaUuid = this.feedback?.MediaUuid
      if (!this.show || !mediaUuid) {
        return
      }
      if (this.mediaFetchInFlight) {
        return
      }
      if (this.lastFetchedMediaUuid === mediaUuid && this.mediaDetail?.SignedUrl) {
        return
      }
      this.fetchMedia(mediaUuid)
    },
    async fetchMedia() {
      const mediaUuid = this.feedback?.MediaUuid
      if (!mediaUuid) {
        this.mediaDetail = {}
        return
      }

      if (this.mediaFetchInFlight) {
        return
      }

      this.isMediaLoading = true
      this.lastFetchedMediaUuid = mediaUuid
      try {
        this.mediaFetchInFlight = getFeedbackMedia(mediaUuid)
        const result = await this.mediaFetchInFlight
        this.mediaDetail = result?.data || {}
      } catch (error) {
        this.mediaDetail = {}
      } finally {
        this.isMediaLoading = false
        this.mediaFetchInFlight = null
      }
    },
    closeModal() {
      this.showImageViewer = false
      this.$emit('close')
    },
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString()
    },
  },
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(8px);
}

.detail-card {
  width: min(1080px, 100%);
  max-height: 94vh;
  overflow: auto;
  border: 1px solid rgba(110, 43, 146, 0.12);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.18);
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 24px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
}

.header-copy {
  min-width: 0;
}

.detail-kicker {
  margin: 0 0 6px;
  color: #7c3aed;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.detail-header h2 {
  margin: 0;
  color: #111827;
  font-size: 1.45rem;
}

.header-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.detail-subtitle {
  margin: 8px 0 0;
  color: #6b7280;
  line-height: 1.5;
  word-break: break-word;
}

.subtitle-meta {
  margin-left: 6px;
  color: #94a3b8;
  font-weight: 600;
}

.close-button {
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 12px;
  background: #f8fafc;
  color: #475569;
}

.hero-media {
  padding: 18px 24px 10px;
  display: flex;
  justify-content: center;
}

.media-shell {
  width: min(520px, 100%);
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: #0f172a;
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
}

.media-button {
  width: 100%;
  border: 0;
  padding: 0;
  display: block;
  position: relative;
  background: transparent;
  cursor: pointer;
}

.media-button img {
  width: 100%;
  height: 340px;
  object-fit: cover;
  display: block;
}

.media-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 14px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0) 45%, rgba(15, 23, 42, 0.72));
  opacity: 0;
  transition: opacity 0.18s ease;
}

.media-overlay-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #111827;
  font-weight: 700;
}

.media-button:hover .media-overlay {
  opacity: 1;
}

.media-placeholder {
  min-height: 340px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #0f172a;
  color: rgba(226, 232, 240, 0.85);
  font-weight: 600;
}

.detail-sections {
  padding: 12px 24px 24px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.section-span {
  grid-column: 1 / -1;
}

.section-card {
  padding: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.05);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.section-title {
  color: #7c3aed;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kv-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.kv {
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.14);
  min-width: 0;
}

.kv-span {
  grid-column: 1 / -1;
}

.kv-label {
  display: block;
  margin-bottom: 8px;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.kv-value {
  display: block;
  color: #111827;
  word-break: break-word;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.sub-meta {
  display: block;
  margin-top: 6px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.inline-meta {
  margin-left: 8px;
  color: #64748b;
  font-weight: 600;
}

.notes {
  white-space: pre-wrap;
}

.outcome-pill,
.grade-pill {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1;
}

.positive-pill {
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
}

.negative-pill {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.grade-pill {
  background: rgba(124, 58, 237, 0.10);
  color: #6d28d9;
}

.fading-enter-active,
.fading-leave-active {
  transition: opacity 0.3s;
}

.fading-enter-from,
.fading-leave-to {
  opacity: 0;
}

@media (max-width: 960px) {
  .media-button img,
  .media-placeholder {
    min-height: 260px;
    height: 260px;
  }

  .detail-sections,
  .kv-grid {
    grid-template-columns: 1fr;
  }
}
</style>
